import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(private readonly msal: MsalService, private readonly router: Router) {}

  private clearMsalLocalStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('msal.')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }

  private async redirectToLogin(stateUrl: string): Promise<boolean> {
    try {
      sessionStorage.setItem('pb.postLoginUrl', stateUrl || '/tabs/inbox');
    } catch {
      // ignore
    }

    await this.router.navigateByUrl('/login', { replaceUrl: true });
    return false;
  }

  async canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const instance = this.msal.instance;

    try {
      await instance.initialize();
    } catch {
      return this.redirectToLogin(state?.url || '/tabs/inbox');
    }

    const accounts = instance.getAllAccounts();
    const active = instance.getActiveAccount() ?? accounts[0];

    if (!active) {
      return this.redirectToLogin(state?.url || '/tabs/inbox');
    }

    try {
      const result: AuthenticationResult = await instance.acquireTokenSilent({
        account: active,
        scopes: environment.apiConfig.scopes,
      });

      if (!instance.getActiveAccount()) {
        instance.setActiveAccount(result.account ?? active);
      }

      return true;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) {
        // eslint-disable-next-line no-console
        console.error('[AuthGuard] Token acquisition failed', err);
      }

      try {
        instance.setActiveAccount(null);
      } catch {
        // ignore
      }

      try {
        await instance.clearCache();
      } catch {
        // ignore
      }

      this.clearMsalLocalStorage();
      return this.redirectToLogin(state?.url || '/tabs/inbox');
    }
  }
}
